/**
 * SentinelReports — state regulatory reporting, Florida AFWAAR (0133) first.
 *
 * The workflow the screen enforces: pick the state fiscal year → see exactly
 * what blocks a clean submission (per-case gaps + missing annual figures) →
 * fill the annual figures → export the agency's own template, completed and
 * named for SFTP submission. Scope is D-SNP/Medicaid cases only; Medicare
 * work is never pulled into this report.
 */
import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

// Default to the most recently completed Florida SFY (July–June): the annual
// report due September 1 covers the year that just ended.
function defaultFy() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const startYear = now.getUTCMonth() + 1 >= 7 ? y - 1 : y - 2;
  return `${startYear}-${startYear + 1}`;
}
function fyOptions() {
  const out = [];
  const anchor = Number(defaultFy().slice(0, 4));
  for (let y = anchor + 1; y >= anchor - 4; y--) out.push(`${y}-${y + 1}`);
  return out;
}

const PREVENTION_OUTCOMES = [
  'Prepayment Reviews', 'Payment Suspensions', 'Terminations',
  'Communication and/or Education Letters', 'Self Audits', 'SIU On-sites for FWA',
  'Other - Please specify below',
];

export default function SentinelReports({ onOpenCase }) {
  const [fy, setFy] = useState(defaultFy());
  const [readiness, setReadiness] = useState(null);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [rRes, iRes] = await Promise.all([
        fetch(`${API}/api/sentinel/reports/afwaar/readiness?fy=${fy}`, { credentials: 'include' }),
        fetch(`${API}/api/sentinel/reports/afwaar/inputs?fy=${fy}`, { credentials: 'include' }),
      ]);
      const rData = await rRes.json();
      const iData = await iRes.json();
      if (rData.success) setReadiness(rData.readiness); else setError(rData.message || 'Could not assess readiness.');
      if (iData.success) setInputs(iData.data || {});
    } catch { setError('Could not load report status.'); }
    setLoading(false);
  }, [fy]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setInputs(prev => ({ ...prev, [k]: v }));
  const setPrevention = (label, key, v) => setInputs(prev => ({
    ...prev,
    prevention: { ...(prev.prevention || {}), [label]: { ...((prev.prevention || {})[label] || {}), [key]: v } },
  }));

  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/sentinel/reports/afwaar/inputs`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fy, data: inputs }),
      });
      const data = await res.json();
      if (data.success) { setSavedAt(new Date()); load(); }
      else setError(data.message || 'Save failed.');
    } catch { setError('Save failed.'); }
    setSaving(false);
  };

  const exportUrl = `${API}/api/sentinel/reports/afwaar/export?fy=${fy}`;

  const inputCls = 'w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-red-500/30 focus:border-red-400 outline-none';
  const labelCls = 'text-[11px] font-semibold text-slate-500 uppercase tracking-wide';

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      {/* Header + FY picker */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Annual FWA Activity Report — Florida (0133)</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Built from your D-SNP and Medicaid cases only, Medicare work is never included. The export fills the agency's own template, ready for the MPI-MCU SFTP site with your attestation. Due September 1 each year for the state fiscal year ending June 30.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={fy} onChange={e => setFy(e.target.value)} className={inputCls + ' w-auto'}>
            {fyOptions().map(o => <option key={o} value={o}>SFY {o}</option>)}
          </select>
          <a
            href={exportUrl}
            className={`px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${readiness?.ready ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-400 hover:bg-slate-500'}`}
            title={readiness?.ready ? 'Download the completed agency template.' : 'You can export now, but the gaps listed below will be blank cells in the submission.'}
          >
            Export workbook
          </a>
        </div>
      </div>

      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">{error}</div>}
      {loading && <div className="text-sm text-slate-400">Checking report readiness…</div>}

      {/* Readiness */}
      {!loading && readiness && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${readiness.ready ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-300'}`}>
              {readiness.ready ? 'Ready to submit' : 'Not ready — gaps below'}
            </span>
            <span className="text-xs text-slate-500">
              {readiness.cases_in_scope} case{readiness.cases_in_scope === 1 ? '' : 's'} in scope · {readiness.detail_rows} detail row{readiness.detail_rows === 1 ? '' : 's'} · {readiness.cases_with_gaps} with missing fields
            </span>
            {readiness.medicare_cases_same_period > 0 && (
              <span className="text-xs text-slate-400">
                ({readiness.medicare_cases_same_period} Medicare case{readiness.medicare_cases_same_period === 1 ? '' : 's'} this period stay out of this report — reclassify line of business on any that are actually D-SNP/Medicaid)
              </span>
            )}
          </div>

          {readiness.missing_inputs?.length > 0 && (
            <div className="mt-3 text-xs text-amber-800">
              Annual figures still needed: {readiness.missing_inputs.join(', ')}. Enter them below.
            </div>
          )}

          {readiness.gaps?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2">Case</th>
                    <th className="px-3 py-2">Missing before submission</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {readiness.gaps.map(g => (
                    <tr key={g.case_id}>
                      <td className="px-3 py-2">
                        <button onClick={() => onOpenCase && onOpenCase(g.case_id)} className="text-red-600 hover:underline font-mono text-xs">
                          {g.case_number}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{g.missing.join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-400 mt-2">Open each case and complete its Florida reporting panel. Fields marked here become blank cells in the agency file if exported as is.</p>
            </div>
          )}
        </div>
      )}

      {/* Annual figures */}
      {!loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-800">Annual figures (entered once per fiscal year)</h3>
            <div className="flex items-center gap-2">
              {savedAt && <span className="text-[11px] text-emerald-600">Saved {savedAt.toLocaleTimeString()}</span>}
              <button onClick={save} disabled={saving}
                className="px-4 py-1.5 rounded-xl bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : 'Save figures'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><div className={labelCls}>Plan name (Overview row 1)</div>
              <input value={inputs.plan_name || ''} onChange={e => setField('plan_name', e.target.value)} className={inputCls} /></div>
            <div><div className={labelCls}>3-letter plan identifier (file name)</div>
              <input value={inputs.plan_identifier || ''} onChange={e => setField('plan_identifier', e.target.value.toUpperCase().slice(0, 3))} placeholder="e.g. SOL" className={inputCls} /></div>
            <div><div className={labelCls}>DSNP Medicaid Provider ID(s)</div>
              <input value={inputs.medicaid_provider_ids || ''} onChange={e => setField('medicaid_provider_ids', e.target.value)} className={inputCls} /></div>
            <div><div className={labelCls}>Total SIU / program-integrity cost ($)</div>
              <input type="number" value={inputs.siu_total_cost ?? ''} onChange={e => setField('siu_total_cost', e.target.value)} className={inputCls} /></div>
            <div><div className={labelCls}>ROI calculation (Overview row 43)</div>
              <input value={inputs.roi ?? ''} onChange={e => setField('roi', e.target.value)} placeholder="e.g. 3.2:1" className={inputCls} /></div>
            <div><div className={labelCls}>Providers prevented / terminated (totals)</div>
              <div className="flex gap-2">
                <input type="number" value={inputs.providers_prevented_total ?? ''} onChange={e => setField('providers_prevented_total', e.target.value)} placeholder="Prevented" className={inputCls} />
                <input type="number" value={inputs.providers_terminated_total ?? ''} onChange={e => setField('providers_terminated_total', e.target.value)} placeholder="Terminated" className={inputCls} />
              </div></div>
          </div>

          <div>
            <div className={labelCls}>ROI methodology summary (ROI tab)</div>
            <textarea rows={2} value={inputs.roi_summary || ''} onChange={e => setField('roi_summary', e.target.value)} className={inputCls + ' resize-none'} />
          </div>
          <div>
            <div className={labelCls}>Overview narrative (Overview row 44)</div>
            <textarea rows={3} value={inputs.narrative || ''} onChange={e => setField('narrative', e.target.value)} className={inputCls + ' resize-none'} />
          </div>

          {/* Prevention activities */}
          <div>
            <div className={labelCls}>Prevention activities (number + cost avoidance)</div>
            <div className="mt-2 space-y-1.5">
              {PREVENTION_OUTCOMES.map(label => (
                <div key={label} className="flex items-center gap-2">
                  <span className="text-xs text-slate-600 w-72 flex-shrink-0">{label}</span>
                  <input type="number" placeholder="Number" value={(inputs.prevention?.[label]?.number) ?? ''}
                    onChange={e => setPrevention(label, 'number', e.target.value)} className={inputCls + ' w-28'} />
                  <input type="number" placeholder="$ avoided" value={(inputs.prevention?.[label]?.savings) ?? ''}
                    onChange={e => setPrevention(label, 'savings', e.target.value)} className={inputCls + ' w-32'} />
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-slate-400">
            Reminder: the agency also requires a signed attestation alongside this workbook, and both are submitted through the MPI-MCU SFTP site. Numbers here must reconcile with your 15-day reports for the year.
          </p>
        </div>
      )}
    </div>
  );
}
